import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import { ADMIN_PATHS, adminPath } from '../constants/adminPaths';

import './admin-tools/AdminTools.css';

const getMetaClass = (meta) => {
  const key = String(meta || '').toLowerCase();
  if (key === 'admins') return 'adminToolsPill adminToolsPill--admins';
  if (key === 'config') return 'adminToolsPill adminToolsPill--config';
  if (key === 'billing') return 'adminToolsPill adminToolsPill--billing';
  if (key === 'logs') return 'adminToolsPill adminToolsPill--logs';
  if (key === 'security') return 'adminToolsPill adminToolsPill--security';
  if (key === 'dev') return 'adminToolsPill adminToolsPill--dev';
  if (key === 'danger') return 'adminToolsPill adminToolsPill--danger';
  return 'adminToolsPill';
};

const ToolCard = ({ title, description, meta, onClick }) => {
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.();
    }
  };

  return (
    <div
      className="adminToolCard"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={onKeyDown}
      aria-label={title}
    >
      <div className="adminToolTop">
        <span className={getMetaClass(meta)}>{meta || 'Tool'}</span>
      </div>

      <h3 className="adminToolTitle">{title}</h3>
      <p className="adminToolDesc">{description}</p>
    </div>
  );
};

const AdminTools = () => {
  const navigate = useNavigate();
  const { admin } = useAdminAuth();
  const isSuperAdmin = admin?.role === 'super-admin';

  return (
    <div className="admin-page adminToolsPage">
      <div className="admin-page__header">
        <div>
          <h1 className="admin-page__title">Admin Tools</h1>
          <p className="admin-page__subtitle">
            Quick access to power tools for managing admins, billing and logs.
          </p>
        </div>
      </div>
      <div className="adminToolsGrid">
        <ToolCard 
          title="Admin Management" 
          description="Create and manage admin users and roles."
          meta="Admins"
          onClick={() => navigate(adminPath(ADMIN_PATHS.ADMIN_MANAGEMENT_CREATE))}
        />
        <ToolCard 
          title="App Settings" 
          description="Control panel name, maintenance modes, and platform email/SMS credentials."
          meta="Config"
          onClick={() => navigate(adminPath(ADMIN_PATHS.APP_SETTINGS))}
        />
        <ToolCard 
          title="Packages" 
          description="View and manage subscription packages."
          meta="Billing"
          onClick={() => navigate(adminPath(ADMIN_PATHS.PACKAGES))}
        />
        <ToolCard
          title="Package grace period preview"
          description="Preview the grace-period banner and expired states used in the business app."
          meta="Dev"
          onClick={() => navigate(adminPath(ADMIN_PATHS.TOOLS_PACKAGE_GRACE_TEST))}
        />
        <ToolCard 
          title="Manual Charge" 
          description="Authorize one-time payments for customers."
          meta="Billing"
          onClick={() => navigate(adminPath(ADMIN_PATHS.PAYMENTS_AUTHORIZE))}
        />
        <ToolCard 
          title="SMS Logs" 
          description="View and monitor all SMS messages sent by the system for security auditing."
          meta="Logs"
          onClick={() => navigate(adminPath(ADMIN_PATHS.SMS_LOGS))}
        />
        <ToolCard 
          title="Impersonation Logs" 
          description="Audit which admins impersonate which users and companies."
          meta="Security"
          onClick={() => navigate(adminPath(ADMIN_PATHS.IMPERSONATION_LOGS))}
        />
        <ToolCard 
          title="Custom Plans" 
          description="Define negotiated plans per customer and send private invite links."
          meta="Billing"
          onClick={() => navigate(adminPath(ADMIN_PATHS.CUSTOM_PLANS))}
        />
        <ToolCard 
          title="Special Access Invites" 
          description="Invite someone by email to create their own account."
          meta="Dev"
          onClick={() => navigate(adminPath(ADMIN_PATHS.SPECIAL_ACCESS_INVITES))}
        />
        <ToolCard 
          title="Patch notes" 
          description="View recent changes and improvements to the app."
          meta="Dev"
          onClick={() => navigate(adminPath(ADMIN_PATHS.PATCH_NOTES))}
        />
        {isSuperAdmin ? (
          <ToolCard
            title="Purge Company"
            description="Permanently delete a test company, its data, and owner/employee auth accounts."
            meta="Danger"
            onClick={() => navigate(adminPath(ADMIN_PATHS.TOOLS_PURGE_COMPANY))}
          />
        ) : null}
      </div>
    </div>
  );
};

export default AdminTools;
 

